import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Stack,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Paper,
  LinearProgress,
  Chip,
  useTheme
} from '@mui/material';
import {
  VolumeUp as SpeakerIcon,
  Delete as DeleteIcon,
  AddAPhoto as AddPhotoIcon,
  Add as AddIcon,
  VideoCall as VideoIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import ZoomableImage from '../../components/shared/ZoomableImage';
import UnitPageShell from '../../components/shared/UnitPageShell';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride, resolveImage, resolveVideo, resolvePdf } from '../../services/unitOverridesService';
import { tokens } from '../../theme/tokens';

const IMAGE_BASE = '/assets/drawable/';

interface DesignBlock {
  id: string;
  type: 'text' | 'image' | 'video' | 'pdf' | 'divider';
  heading?: string;
  body?: string;
  imageKey?: string;
  imageSrc?: string;
  videoKey?: string;
  videoSrc?: string;
  pdfKey?: string;
  pdfSrc?: string;
  fontSize?: number;
  fontWeight?: 'normal' | 'bold';
  textColor?: string;
  backgroundColor?: string;
  alignment?: 'left' | 'center' | 'right';
  padding?: number;
  caption?: string; // Beschriftung für Medien
}

interface PageDesign {
  title: string;
  blocks: DesignBlock[];
  backgroundColor?: string;
  accentColor?: string;
  requiredLevel?: number;
  customTitle?: string;
}

const createStepByStepPage = (pageId: string): React.FC => {
  const PAGE_ID = pageId;

  const Page: React.FC = () => {
    const theme = useTheme();
    const darkMode = theme.palette.mode === 'dark';
    const { token, canEditStepByStep, canUploadStepByStepMedia, canManageStepByStepVisibility } = useAuth();
    const isAdmin = canEditStepByStep;
    const canUploadMedia = canUploadStepByStepMedia;
    const canToggleVisibility = canManageStepByStepVisibility;
    
    // Token-based color helpers
    const textPrimary = darkMode ? tokens.colors.dark.text : tokens.colors.light.text;
    const surfaceAlt = darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt;
    const accentColor = tokens.colors.primary;
    const borderColor = darkMode ? tokens.colors.dark.border : tokens.colors.light.border;

    const [designMode, setDesignMode] = useState(false);
    const [hiddenFlag, setHiddenFlag] = useState(false);
    const [currentDesign, setCurrentDesign] = useState<PageDesign | null>(null);
    const [draftDesign, setDraftDesign] = useState<PageDesign | null>(null);
    const saveTimer = useRef<NodeJS.Timeout | null>(null);

    const { override, refresh } = useUnitOverride(PAGE_ID);

    useEffect(() => {
      if (override?.design && typeof override.design === 'object' && Object.keys(override.design).length > 0 && override.design.blocks) {
        setCurrentDesign(override.design as PageDesign);
        setDraftDesign(override.design as PageDesign);
      }
      if (typeof override?.hidden === 'boolean') {
        setHiddenFlag(override.hidden);
      }
    }, [override]);

    const pageDesign: PageDesign = useMemo(() => {
      // Verwende draftDesign für die aktuelle Anzeige (sofort aktualisiert)
      if (draftDesign && draftDesign.blocks && Array.isArray(draftDesign.blocks)) {
        return draftDesign;
      }
      // Fallback auf currentDesign
      if (currentDesign && currentDesign.blocks && Array.isArray(currentDesign.blocks)) {
        return currentDesign;
      }
      if (override?.design && typeof override.design === 'object' && Array.isArray(override.design.blocks)) {
        return override.design as PageDesign;
      }
      const darkMode = false;
      return {
        title: 'Step-by-Step Anleitung',
        backgroundColor: darkMode ? tokens.colors.dark.surface : tokens.colors.light.surfaceAlt,
        accentColor: tokens.colors.primary,
        blocks: [
          {
            id: 'block_0',
            type: 'text',
            heading: 'Willkommen zur Anleitung',
            body: 'Dies ist eine Platzhalter-Seite. Im Admin-Modus können Sie diese Seite komplett gestalten: Texte, Bilder, Farben, Layouts.',
            fontSize: 16,
            fontWeight: 'normal',
            textColor: darkMode ? tokens.colors.dark.text : tokens.colors.light.text,
            alignment: 'left',
            padding: 16
          }
        ]
      };
    }, [draftDesign, currentDesign, override?.design]);

    const saveDesign = useCallback(async (newDesign: PageDesign) => {
      if (!token || !isAdmin) return;
      try {
        const result = await updateUnitContent(PAGE_ID, { design: newDesign }, token);
        if (result?.design) {
          setCurrentDesign(result.design as PageDesign);
        } else {
          setCurrentDesign(newDesign);
        }
      } catch (e: any) {
        await refresh();
      }
    }, [token, isAdmin, refresh]);

    const queueSaveDesign = useCallback((newDesign: PageDesign) => {
      // Update draft immediately so the UI reflects changes without delay.
      setDraftDesign(newDesign);
      if (saveTimer.current) clearTimeout(saveTimer.current as any);
      saveTimer.current = setTimeout(() => {
        setCurrentDesign(newDesign);
        saveDesign(newDesign);
      }, 1500);
    }, [saveDesign]);

    const forceSave = useCallback(() => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current as any);
        saveTimer.current = null;
      }
      if (draftDesign) {
        setCurrentDesign(draftDesign);
        saveDesign(draftDesign);
      }
    }, [draftDesign, saveDesign]);

    const toggleHidden = useCallback(async (nextHidden: boolean) => {
      if (!token || !canToggleVisibility) {
        alert('Keine Berechtigung, die Sichtbarkeit zu aendern.');
        return;
      }
      try {
        await updateUnitContent(PAGE_ID, { hidden: nextHidden }, token);
        setHiddenFlag(nextHidden);
      } catch (e: any) {
        alert('Änderung konnte nicht gespeichert werden.');
        setHiddenFlag(!!override?.hidden);
      }
    }, [token, canToggleVisibility, override?.hidden]);

    const addBlock = useCallback((type: 'text' | 'image' | 'video' | 'pdf' | 'divider') => {
      try {
        const newBlockId = `block_${Date.now()}`;
        const newBlock: DesignBlock = {
          id: newBlockId,
          type,
          ...(type === 'text' && {
            heading: 'Neuer Textblock',
            body: 'Hier können Sie Text eingeben...',
            fontSize: 14,
            fontWeight: 'normal',
            textColor: '#1f1f1f',
            alignment: 'left',
            padding: 12
          }),
          ...(type === 'image' && {
            imageKey: `img_${Date.now()}`,
            imageSrc: `${IMAGE_BASE}placeholder.png`,
            caption: ''
          }),
          ...(type === 'video' && {
            videoKey: `video_${Date.now()}`,
            videoSrc: '',
            caption: 'Video-Beschreibung'
          }),
          ...(type === 'pdf' && {
            pdfKey: `pdf_${Date.now()}`,
            pdfSrc: '',
            caption: 'PDF-Dokument'
          })
        };

        const updatedDesign = {
          ...pageDesign,
          blocks: [...pageDesign.blocks, newBlock]
        };
        queueSaveDesign(updatedDesign);
      } catch (e: any) {
        alert('❌ Fehler beim Hinzufügen: ' + e?.message);
      }
    }, [pageDesign, queueSaveDesign]);

    const insertBlockAt = useCallback((index: number, type: 'text' | 'image' | 'video' | 'pdf' | 'divider') => {
      const newBlockId = `block_${Date.now()}`;
      const newBlock: DesignBlock = {
        id: newBlockId,
        type,
        ...(type === 'text' && {
          heading: 'Neuer Textblock',
          body: 'Hier können Sie Text eingeben...',
          fontSize: 14,
          fontWeight: 'normal',
          textColor: '#1f1f1f',
          alignment: 'left',
          padding: 12
        }),
        ...(type === 'image' && {
          imageKey: `img_${Date.now()}`,
          imageSrc: `${IMAGE_BASE}placeholder.png`,
          caption: ''
        }),
        ...(type === 'video' && {
          videoKey: `video_${Date.now()}`,
          videoSrc: '',
          caption: 'Video-Beschreibung'
        }),
        ...(type === 'pdf' && {
          pdfKey: `pdf_${Date.now()}`,
          pdfSrc: '',
          caption: 'PDF-Dokument'
        })
      };
      const newBlocks = [...pageDesign.blocks];
      newBlocks.splice(index, 0, newBlock);
      queueSaveDesign({ ...pageDesign, blocks: newBlocks });
    }, [pageDesign, queueSaveDesign]);

    const moveBlock = useCallback((blockId: string, direction: 'up' | 'down') => {
      const idx = pageDesign.blocks.findIndex(b => b.id === blockId);
      if (idx === -1) return;
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= pageDesign.blocks.length) return;
      const newBlocks = [...pageDesign.blocks];
      const [removed] = newBlocks.splice(idx, 1);
      newBlocks.splice(target, 0, removed);
      queueSaveDesign({ ...pageDesign, blocks: newBlocks });
    }, [pageDesign, queueSaveDesign]);

    const deleteBlock = useCallback((blockId: string) => {
      const updatedDesign = {
        ...pageDesign,
        blocks: pageDesign.blocks.filter(b => b.id !== blockId)
      };
      queueSaveDesign(updatedDesign);
    }, [pageDesign, queueSaveDesign]);

    const updateBlock = useCallback((blockId: string, updates: Partial<DesignBlock>) => {
      const updatedDesign = {
        ...pageDesign,
        blocks: pageDesign.blocks.map(b =>
          b.id === blockId ? { ...b, ...updates } : b
        )
      };
      queueSaveDesign(updatedDesign);
    }, [pageDesign, queueSaveDesign]);

    const fullText = useMemo(() => {
      if (!pageDesign?.blocks || !Array.isArray(pageDesign.blocks)) return '';
      return pageDesign.blocks
        .filter(b => b.type === 'text')
        .map(b => `${b.heading || ''} ${b.body || ''}`)
        .join(' ');
    }, [pageDesign]);

    const { speak, stop, speaking } = useTTS({ text: fullText });

    // Token-basierte Farben für Admin UI
    const adminBgLight = '#fafafa';  // Admin editor background
    const adminBgAlt = '#f0f0f0';    // Admin editor alternate
    const statusErrorBg = '#ffebee'; // Error feedback
    const statusSuccessBg = '#e8f5e9'; // Success feedback
    const statusWarnBg = '#fff3e0';  // Warning feedback
    const iconGrayColor = '#9e9e9e'; // Gray icons
    const iconOrangeColor = '#ff5722'; // Orange icons
    const dashedBorder = darkMode ? `1px dashed ${borderColor}` : '1px dashed #ccc';

    // Upload-Status
    const [uploadingSlot, setUploadingSlot] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState<string>('');
    const [uploadPercent, setUploadPercent] = useState<number>(0);

    // Allgemeiner Media-Upload-Handler für alle Medientypen
    const handleMediaUpload = useCallback(async (
      blockId: string, 
      slot: string, 
      file: File,
      mediaType: 'image' | 'video' | 'pdf'
    ) => {
      if (!token) return alert('Nicht authentifiziert.');
      if (!canUploadMedia) return alert('Keine Berechtigung fuer Medien-Uploads.');
      
      setUploadingSlot(slot);
      setUploadProgress('Hochladen...');
      setUploadPercent(0);
      
      try {
        const result = await uploadUnitAsset(PAGE_ID, slot, file, token, (percent) => {
          setUploadPercent(percent);
          setUploadProgress(`Hochladen... ${percent}%`);
        });
        setUploadPercent(100);
        
        // Feedback über Komprimierung
        if (result.compressed) {
          const savedMB = ((result.originalSize - result.finalSize) / 1024 / 1024).toFixed(1);
          const finalMB = (result.finalSize / 1024 / 1024).toFixed(1);
          setUploadProgress(`Komprimiert: Ersparnis ${savedMB} MB (${finalMB} MB final)`);
          setTimeout(() => setUploadProgress(''), 3000);
        } else {
          setUploadProgress('Hochgeladen.');
          setTimeout(() => setUploadProgress(''), 2000);
        }
        
        await refresh();
      } catch (e: any) {
        setUploadProgress(`Fehler: ${e?.message}`);
        setTimeout(() => setUploadProgress(''), 5000);
      } finally {
        setUploadingSlot(null);
        setTimeout(() => setUploadPercent(0), 1000);
      }
    }, [token, canUploadMedia, PAGE_ID, refresh]);

    // Legacy handler for backward compatibility
    const handleImageReplace = useCallback(async (blockId: string, imageKey: string, file: File) => {
      return handleMediaUpload(blockId, imageKey, file, 'image');
    }, [handleMediaUpload]);

    if (isAdmin && designMode) {
      return (
        <Box sx={{ p: 3, backgroundColor: adminBgLight, minHeight: '100vh' }}>
          <Paper sx={{ p: 2, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5">🎨 Design-Modus: {pageDesign.title}</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControlLabel
                control={<Switch checked={hiddenFlag} onChange={(e) => toggleHidden(e.target.checked)} disabled={!canToggleVisibility} />}
                label="Seite im Menü verstecken"
              />
              <Button variant="contained" color="success" onClick={forceSave}>
                💾 Jetzt Speichern
              </Button>
              <FormControlLabel
                control={<Switch checked={designMode} onChange={(e) => setDesignMode(e.target.checked)} />}
                label="Design-Modus"
              />
            </Stack>
          </Paper>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" mb={2}>
              ⚙️ Seiten-Einstellungen
            </Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Seitentitel (im Editor)"
                value={pageDesign.title}
                onChange={(e) => {
                  const updated = { ...pageDesign, title: e.target.value };
                  queueSaveDesign(updated);
                }}
              />
              <TextField
                fullWidth
                label="Titel im Menü (optional)"
                value={pageDesign.customTitle || ''}
                placeholder="Leer = Standard-Titel verwenden"
                onChange={(e) => {
                  const updated = { ...pageDesign, customTitle: e.target.value };
                  queueSaveDesign(updated);
                }}
                helperText="Dieser Titel wird im Navigationsmenü angezeigt"
              />
              <TextField
                fullWidth
                label="Mindestpunktzahl"
                type="number"
                value={pageDesign.requiredLevel ?? 0}
                onChange={(e) => {
                  const updated = { ...pageDesign, requiredLevel: parseInt(e.target.value) || 0 };
                  queueSaveDesign(updated);
                }}
                helperText="Benötigte Punkte um diese Seite zu sehen (0 = für alle sichtbar)"
              />
              <TextField
                fullWidth
                label="Akzent-Farbe"
                type="color"
                value={pageDesign.accentColor || '#B2140F'}
                onChange={(e) => {
                  const updated = { ...pageDesign, accentColor: e.target.value };
                  queueSaveDesign(updated);
                }}
              />
            </Stack>
          </Paper>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" mb={2}>
              📋 Blöcke verwalten
            </Typography>
            <Stack spacing={2} mb={2} direction="row" flexWrap="wrap" gap={1}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => addBlock('text')}>
                Textblock
              </Button>
              <Button variant="contained" startIcon={<AddPhotoIcon />} onClick={() => addBlock('image')}>
                Bild/GIF
              </Button>
              <Button variant="contained" color="secondary" startIcon={<VideoIcon />} onClick={() => addBlock('video')}>
                Video
              </Button>
              <Button variant="contained" color="info" startIcon={<PdfIcon />} onClick={() => addBlock('pdf')}>
                PDF
              </Button>
              <Button variant="contained" onClick={() => addBlock('divider')}>
                Trennlinie
              </Button>
            </Stack>

            {/* Upload-Status Anzeige */}
            {uploadProgress && (
              <Paper sx={{ p: 1, mb: 2, backgroundColor: uploadProgress.includes('❌') ? statusErrorBg : statusSuccessBg }}>
                <Typography variant="body2">{uploadProgress}</Typography>
                {uploadingSlot && (
                  <LinearProgress
                    sx={{ mt: 1 }}
                    variant="determinate"
                    value={uploadPercent}
                  />
                )}
              </Paper>
            )}

            <Stack spacing={2}>
              {(pageDesign?.blocks || []).map((block, idx) => (
                <Card key={block.id} sx={{ p: 2, backgroundColor: adminBgLight }}>
                  <CardContent sx={{ p: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2">
                        Block {idx}: {block.type}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Button size="small" variant="outlined" onClick={() => moveBlock(block.id, 'up')} disabled={idx === 0}>↑</Button>
                        <Button size="small" variant="outlined" onClick={() => moveBlock(block.id, 'down')} disabled={idx === (pageDesign?.blocks?.length || 0) - 1}>↓</Button>
                        <Divider flexItem orientation="vertical" />
                        <Button size="small" startIcon={<AddIcon />} onClick={() => insertBlockAt(idx, 'text')}>+Text</Button>
                        <Button size="small" startIcon={<AddPhotoIcon />} onClick={() => insertBlockAt(idx, 'image')}>+Bild</Button>
                        <Button size="small" startIcon={<VideoIcon />} onClick={() => insertBlockAt(idx, 'video')}>+Video</Button>
                        <Button size="small" startIcon={<PdfIcon />} onClick={() => insertBlockAt(idx, 'pdf')}>+PDF</Button>
                        <Divider flexItem orientation="vertical" />
                        <IconButton size="small" onClick={() => deleteBlock(block.id)} color="error" title="Löschen">
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Box>

                    {block.type === 'text' && (
                      <Stack spacing={2}>
                        <TextField
                          fullWidth
                          label="Überschrift"
                          value={block.heading || ''}
                          onChange={(e) => updateBlock(block.id, { heading: e.target.value })}
                          size="small"
                        />
                        <TextField
                          fullWidth
                          label="Text"
                          value={block.body || ''}
                          onChange={(e) => updateBlock(block.id, { body: e.target.value })}
                          multiline
                          rows={3}
                          size="small"
                        />
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                          <TextField
                            label="Schriftgröße"
                            type="number"
                            value={block.fontSize || 14}
                            onChange={(e) => updateBlock(block.id, { fontSize: parseInt(e.target.value) })}
                            size="small"
                          />
                          <TextField
                            label="Textfarbe"
                            type="color"
                            value={block.textColor || '#1f1f1f'}
                            onChange={(e) => updateBlock(block.id, { textColor: e.target.value })}
                            size="small"
                          />
                          <TextField
                            label="Hintergrund"
                            type="color"
                            value={block.backgroundColor || '#ffffff'}
                            onChange={(e) => updateBlock(block.id, { backgroundColor: e.target.value })}
                            size="small"
                          />
                          <TextField
                            select
                            label="Ausrichtung"
                            value={block.alignment || 'left'}
                            onChange={(e) => updateBlock(block.id, { alignment: e.target.value as any })}
                            size="small"
                            SelectProps={{ native: true }}
                          >
                            <option value="left">Links</option>
                            <option value="center">Zentriert</option>
                            <option value="right">Rechts</option>
                          </TextField>
                        </Box>
                      </Stack>
                    )}

                    {block.type === 'image' && (
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                          <Chip label="Bild/GIF" size="small" icon={<AddPhotoIcon />} />
                          <Typography variant="caption">Slot: {block.imageKey}</Typography>
                        </Stack>
                        <TextField
                          fullWidth
                          size="small"
                          label="Beschriftung (optional)"
                          value={block.caption || ''}
                          onChange={(e) => updateBlock(block.id, { caption: e.target.value })}
                          sx={{ mb: 1 }}
                        />
                        <Box sx={{ mb: 1, p: 1, border: dashedBorder, borderRadius: 1 }}>
                          {block.imageSrc ? (
                            <img
                              src={block.imageSrc}
                              alt="Preview"
                              style={{ maxWidth: '100%', height: 'auto', maxHeight: '200px' }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">Kein Bild hochgeladen</Typography>
                          )}
                        </Box>
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<AddPhotoIcon />}
                          disabled={uploadingSlot === block.imageKey || !canUploadMedia}
                        >
                          {uploadingSlot === block.imageKey ? 'Lädt...' : 'Bild/GIF hochladen'}
                          <input
                            type="file"
                            accept="image/*,.gif"
                            hidden
                            onChange={(e) => {
                              const file = e.currentTarget.files?.[0];
                              if (file && block.imageKey) {
                                handleMediaUpload(block.id, block.imageKey, file, 'image');
                              }
                            }}
                          />
                        </Button>
                      </Box>
                    )}

                    {block.type === 'video' && (
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                          <Chip label="Video" size="small" color="secondary" icon={<VideoIcon />} />
                          <Typography variant="caption">Slot: {block.videoKey}</Typography>
                        </Stack>
                        <TextField
                          fullWidth
                          size="small"
                          label="Video-Beschriftung"
                          value={block.caption || ''}
                          onChange={(e) => updateBlock(block.id, { caption: e.target.value })}
                          sx={{ mb: 1 }}
                        />
                        <Box sx={{ mb: 1, p: 1, border: dashedBorder, borderRadius: 1, backgroundColor: adminBgAlt }}>
                          {block.videoSrc ? (
                            <video
                              src={block.videoSrc}
                              controls
                              style={{ maxWidth: '100%', maxHeight: '200px' }}
                            />
                          ) : (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                              <VideoIcon sx={{ fontSize: 48, color: iconGrayColor }} />
                              <Typography variant="body2" color="text.secondary">Kein Video hochgeladen</Typography>
                            </Box>
                          )}
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={<VideoIcon />}
                            color="secondary"
                            disabled={uploadingSlot === block.videoKey || !canUploadMedia}
                          >
                            {uploadingSlot === block.videoKey ? 'Lädt... (Videos werden auto-komprimiert)' : 'Video hochladen (MP4, WebM)'}
                            <input
                              type="file"
                              accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
                              hidden
                              onChange={(e) => {
                                const file = e.currentTarget.files?.[0];
                                if (file && block.videoKey) {
                                  handleMediaUpload(block.id, block.videoKey, file, 'video');
                                }
                              }}
                            />
                          </Button>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Videos über 20 MB werden automatisch komprimiert (falls ffmpeg verfügbar)
                        </Typography>
                      </Box>
                    )}

                    {block.type === 'pdf' && (
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                          <Chip label="PDF" size="small" color="info" icon={<PdfIcon />} />
                          <Typography variant="caption">Slot: {block.pdfKey}</Typography>
                        </Stack>
                        <TextField
                          fullWidth
                          size="small"
                          label="PDF-Titel"
                          value={block.caption || ''}
                          onChange={(e) => updateBlock(block.id, { caption: e.target.value })}
                          sx={{ mb: 1 }}
                        />
                        <Box sx={{ mb: 1, p: 2, border: dashedBorder, borderRadius: 1, backgroundColor: statusWarnBg }}>
                          {block.pdfSrc ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <PdfIcon color="error" />
                              <Typography variant="body2">PDF hochgeladen</Typography>
                              <Button size="small" href={block.pdfSrc} target="_blank">Öffnen</Button>
                            </Stack>
                          ) : (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                              <PdfIcon sx={{ fontSize: 48, color: iconOrangeColor }} />
                              <Typography variant="body2" color="text.secondary">Kein PDF hochgeladen</Typography>
                            </Box>
                          )}
                        </Box>
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<PdfIcon />}
                          color="info"
                          disabled={uploadingSlot === block.pdfKey || !canUploadMedia}
                        >
                          {uploadingSlot === block.pdfKey ? 'Lädt...' : 'PDF hochladen'}
                          <input
                            type="file"
                            accept="application/pdf,.pdf"
                            hidden
                            onChange={(e) => {
                              const file = e.currentTarget.files?.[0];
                              if (file && block.pdfKey) {
                                handleMediaUpload(block.id, block.pdfKey, file, 'pdf');
                              }
                            }}
                          />
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Paper>
        </Box>
      );
    }

    return (
      <UnitPageShell
        title={pageDesign.title}
        actions={
          isAdmin ? (
            <Stack direction="row" spacing={1}>
              <IconButton onClick={() => setDesignMode(true)} title="Design-Modus" color="primary">
                ✏️
              </IconButton>
              <IconButton onClick={speaking ? stop : speak} title={speaking ? 'Stopp' : 'Vorlesen'} color="primary">
                <SpeakerIcon />
              </IconButton>
            </Stack>
          ) : (
            <IconButton onClick={speaking ? stop : speak} title={speaking ? 'Stopp' : 'Vorlesen'}>
              <SpeakerIcon />
            </IconButton>
          )
        }
      >
        <Box
          sx={{
            backgroundColor: pageDesign.backgroundColor,
            borderRadius: 1,
            p: 3
          }}
        >
          <Stack spacing={3}>
            {(pageDesign?.blocks || []).map((block, idx) => {
              if (block.type === 'divider') {
                return <Divider key={block.id} />;
              }

              if (block.type === 'text') {
                return (
                  <Box
                    key={block.id}
                    sx={{
                      textAlign: block.alignment as any,
                      backgroundColor: block.backgroundColor,
                      padding: block.padding,
                      borderRadius: 1
                    }}
                  >
                    {block.heading && (
                      <Typography
                        variant="h5"
                        sx={{
                          fontSize: (block.fontSize || 14) + 6,
                          fontWeight: block.fontWeight === 'bold' ? 'bold' : 'normal',
                          color: pageDesign.accentColor,
                          mb: 1
                        }}
                      >
                        {block.heading}
                      </Typography>
                    )}
                    {block.body && (
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: block.fontSize,
                          color: block.textColor,
                          lineHeight: 1.6
                        }}
                      >
                        {block.body}
                      </Typography>
                    )}
                  </Box>
                );
              }

              if (block.type === 'image') {
                const computedSrc = block.imageKey
                  ? resolveImage(block.imageSrc || `${IMAGE_BASE}placeholder.png`, PAGE_ID, block.imageKey, override)
                  : (block.imageSrc || `${IMAGE_BASE}placeholder.png`);
                return (
                  <Box key={block.id}>
                    <ZoomableImage
                      src={computedSrc}
                      alt={block.caption || `Bild ${idx}`}
                      label={canUploadMedia ? '📷 Bild einfügen' : undefined}
                      onReplace={
                        canUploadMedia && block.imageKey
                          ? (file: File) => handleImageReplace(block.id, block.imageKey!, file)
                          : undefined
                      }
                    />
                    {block.caption && (
                      <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: 'text.secondary' }}>
                        {block.caption}
                      </Typography>
                    )}
                  </Box>
                );
              }

              if (block.type === 'video') {
                const computedSrc = block.videoKey
                  ? resolveVideo(block.videoSrc || '', PAGE_ID, block.videoKey, override)
                  : block.videoSrc;
                
                if (!computedSrc) {
                  return canUploadMedia ? (
                    <Paper key={block.id} sx={{ p: 3, textAlign: 'center', backgroundColor: adminBgLight }}>
                      <VideoIcon sx={{ fontSize: 48, color: iconGrayColor, mb: 1 }} />
                      <Typography color="text.secondary">Video noch nicht hochgeladen</Typography>
                      <Button
                        variant="outlined"
                        component="label"
                        sx={{ mt: 2 }}
                        startIcon={<VideoIcon />}
                      >
                        Video hochladen
                        <input
                          type="file"
                          accept="video/mp4,video/webm,video/quicktime"
                          hidden
                          onChange={(e) => {
                            const file = e.currentTarget.files?.[0];
                            if (file && block.videoKey) {
                              handleMediaUpload(block.id, block.videoKey, file, 'video');
                            }
                          }}
                        />
                      </Button>
                    </Paper>
                  ) : null;
                }

                return (
                  <Box key={block.id}>
                    <Paper sx={{ overflow: 'hidden', backgroundColor: '#000' }}>
                      <video
                        src={computedSrc}
                        controls
                        playsInline
                        preload="metadata"
                        style={{ width: '100%', display: 'block' }}
                      >
                        Ihr Browser unterstützt das Video-Tag nicht.
                      </video>
                    </Paper>
                    {block.caption && (
                      <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: 'text.secondary' }}>
                        {block.caption}
                      </Typography>
                    )}
                  </Box>
                );
              }

              if (block.type === 'pdf') {
                const computedSrc = block.pdfKey
                  ? resolvePdf(block.pdfSrc || '', PAGE_ID, block.pdfKey, override)
                  : block.pdfSrc;
                
                if (!computedSrc) {
                  return canUploadMedia ? (
                    <Paper key={block.id} sx={{ p: 3, textAlign: 'center', backgroundColor: statusWarnBg }}>
                      <PdfIcon sx={{ fontSize: 48, color: iconOrangeColor, mb: 1 }} />
                      <Typography color="text.secondary">PDF noch nicht hochgeladen</Typography>
                      <Button
                        variant="outlined"
                        component="label"
                        sx={{ mt: 2 }}
                        startIcon={<PdfIcon />}
                        color="warning"
                      >
                        PDF hochladen
                        <input
                          type="file"
                          accept="application/pdf,.pdf"
                          hidden
                          onChange={(e) => {
                            const file = e.currentTarget.files?.[0];
                            if (file && block.pdfKey) {
                              handleMediaUpload(block.id, block.pdfKey, file, 'pdf');
                            }
                          }}
                        />
                      </Button>
                    </Paper>
                  ) : null;
                }

                return (
                  <Paper key={block.id} sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PdfIcon color="error" sx={{ fontSize: 32 }} />
                        <Typography variant="subtitle1" fontWeight="medium">
                          {block.caption || 'PDF-Dokument'}
                        </Typography>
                      </Stack>
                      <Button
                        variant="contained"
                        color="error"
                        href={computedSrc}
                        target="_blank"
                        startIcon={<PdfIcon />}
                      >
                        PDF öffnen
                      </Button>
                    </Stack>
                    {/* Optional: Eingebettete PDF-Vorschau */}
                    <Box
                      sx={{
                        mt: 2,
                        height: { xs: 900, md: 1200 },
                        minHeight: 900,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}
                    >
                      <iframe
                        src={`${computedSrc}#view=FitH`}
                        width="100%"
                        height="100%"
                        style={{ border: 'none' }}
                        title={block.caption || 'PDF Vorschau'}
                      />
                    </Box>
                  </Paper>
                );
              }

              return null;
            })}
          </Stack>
        </Box>
      </UnitPageShell>
    );
  };

  return Page;
};

export default createStepByStepPage;

